<template>
  <AppDialog
    class="insights-unusual-activity-info-dialog"
    append-to-body
    size="medium"
    title="What is unusual activity?"
    :show="show"
    @close="$emit('close')"
  >
    <p class="info-callout">
      Unusual activity is not a proof of wrongdoing, but a signal worth reviewing.
    </p>
    <div
      v-for="item in explainerItems"
      :key="item.title"
      class="info-item"
    >
      <span class="info-item-icon">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            v-for="(pathD, index) in item.iconPaths"
            :key="index"
            :d="pathD"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </span>
      <div class="info-item-content">
        <h5 class="info-item-title">{{ item.title }}</h5>
        <p class="info-item-description">{{ item.description }}</p>
      </div>
    </div>
    <ul class="info-classifications">
      <li
        v-for="classification in classifications"
        :key="classification.confidenceLevel"
        class="info-classification"
      >
        <span class="info-classification-pill">
          <InsightsUnusualActivityConfidenceBadge :confidence-level="classification.confidenceLevel" />
        </span>
        <span class="info-classification-description">{{ classification.description }}</span>
      </li>
    </ul>
    <template #footer>
      <AppButton
        data-qa="ua-info-go-back"
        type="default"
        @click="$emit('close')"
      >
        Go back
      </AppButton>
      <AppButton
        data-qa="ua-info-read-article"
        type="primary"
        @click="openArticle"
      >
        Read the full article
      </AppButton>
    </template>
  </AppDialog>
</template>

<script>
import AppButton from '~/components/AppButton.vue';
import AppDialog from '~/components/AppDialog.vue';
import InsightsUnusualActivityConfidenceBadge from '~/components/insights/InsightsUnusualActivityConfidenceBadge.vue';

const SUPPORT_ARTICLE_URL = 'https://support.hubstaff.com/hubstaff-insights-unusual-activity/';

export default {
  name: 'InsightsUnusualActivityInfoDialog',
  components: {
    AppButton,
    AppDialog,
    InsightsUnusualActivityConfidenceBadge,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
      required: true,
    },
  },
  data: () => ({
    // Stroke-icon paths drawn to match the design's line-icon style (trend line,
    // two-person outline, stepped flag) — no matching glyphs exist in the fa/hi fonts.
    explainerItems: [
      {
        iconPaths: ['M3.5 16.5l6-6 3.6 3.6 7.4-7.6'],
        title: 'What is tracked',
        description: 'Hubstaff records keyboard, mouse, app usage patterns and inactivity.',
      },
      {
        iconPaths: [
          'M13 7.25a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z',
          'M2.5 19.5v-.9a4.6 4.6 0 0 1 4.6-4.6h3.8a4.6 4.6 0 0 1 4.6 4.6v.9',
          'M16.2 3.55a4 4 0 0 1 0 7.4',
          'M18.6 14.35a4.6 4.6 0 0 1 2.9 4.25v.9',
        ],
        title: 'Patterns analyzed for deviation',
        description: 'Each session is measured against defined thresholds to detect unusual activity.',
      },
      {
        iconPaths: [
          'M6 21V3.5',
          'M6 4.5h6.6l.5 2h4.9v7.5h-6.1l-.5-2H6',
        ],
        title: 'Instances get flagged and classified',
        description: 'Sessions that cross thresholds are assigned a severity level based on deviation.',
      },
    ],
    // Order matches the design: most severe first (see INSIGHTS_CONFIDENCE_LEVELS indexes).
    classifications: [
      { confidenceLevel: 2, description: 'Strong deviation. Highest priority to review.' },
      { confidenceLevel: 1, description: 'Noticeable deviation. Often has a simple explanation.' },
      { confidenceLevel: 0, description: 'Minor deviation. Review when other signals are also present.' },
    ],
  }),
  methods: {
    openArticle() {
      window.open(SUPPORT_ARTICLE_URL, '_blank', 'noopener');
    },
  },
};
</script>
